import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import { authApi } from '../services/api';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component that manages user session state
 * Provides login, register, and logout functionality to child components
 * @param children - Child components that will have access to auth context
 * @returns The auth provider wrapper component
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Verifies stored token on initial load
   */
  useEffect(() => {
    const verifyToken = async (): Promise<void> => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await authApi.verify();
        setUser(response.user);
      } catch {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  /**
   * Authenticates a user with username and password
   * @param username - The user's username
   * @param password - The user's password
   * @throws ApiError if authentication fails
   */
  const login = async (username: string, password: string): Promise<void> => {
    const response = await authApi.login(username, password);
    localStorage.setItem('token', response.token);
    setToken(response.token);
    setUser(response.user);
  };

  /**
   * Registers a new user account
   * @param username - The desired username
   * @param password - The desired password
   * @throws ApiError if registration fails
   */
  const register = async (username: string, password: string): Promise<void> => {
    const response = await authApi.register(username, password);
    localStorage.setItem('token', response.token);
    setToken(response.token);
    setUser(response.user);
  };

  /**
   * Logs out the current user and clears session data
   */
  const logout = (): void => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context
 * @returns The authentication context value
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export default AuthContext;
